/* eslint-disable camelcase */
import { Profile } from 'auth/Auth';
import { MetaData } from 'common/response-editor/MetadataParser';
import { Filter } from 'core/util/Search';

export type EditorMode =
    | 'POST_PUBLISH'
    | 'CONTENT_PUBLISH'
    | 'CAMPAIGN_PUBLISH'
    | 'CAMPAIGN_CREATE'
    | 'CONTENT_CREATE'
    | 'DRAFT';

export type LocationUid = number;

export type PostType = 'MEDIA' | 'LINK';

export type SourceName =
    | 'FACEBOOK'
    | 'INSTAGRAM'
    | 'TWITTER'
    | 'GOOGLE_PLACES'
    | 'LINKEDIN'
    | 'YOUTUBE'
    | 'IG_STORIES'
    | 'IG_REELS'
    | 'TIKTOK';

export type PublishType = 'PUBLISH_NOW' | 'PUBLISH_LATER' | 'OPTIMAL_TIMES' | 'MANUAL' | 'DRAFT';

export enum PublishSourceType {
    API = 1,
    NON_API = 2,
}

export enum PublishPostType {
    REGULAR = 0,
    STORY = 1,
    REEL = 2,
}

export type ComponentType =
    | 'PUBLISHER'
    | 'SOURCE'
    | 'LOCATION'
    | 'TITLE'
    | 'CONTENT'
    | 'GOOGLE_FIELDS'
    | 'YOUTUBE_FIELDS'
    | 'MEDIA'
    | 'LINK'
    | 'TAGS'
    | 'LABELS'
    | 'DATETIME'
    | 'SUBMIT';

// TODO: This will change to just the strings
export type TokenState = 'VALID' | 'INVALID' | 'MISSING' | number | undefined;

// location groups associated with custom content and assets
export type LocationGroup = 'ALL_LOCATIONS' | 'MY_LOCATIONS' | 'MY_LOCATION' | 'SELECT_LOCATIONS';

export interface PublisherConfig {
    editorMode: EditorMode;
    postType?: PostType;
    postId?: string;
    contentId?: string;
    selectedSources?: SourceName[];
    selectedLocations?: LocationUid[];
    title?: string;
    content?: MetaData[];
    media?: any[];
    publishDate?: string; // ISO Date String
    publishType?: PublishType;
    isSourcesLocked?: boolean;
    isLocationsLocked?: boolean;
    isLocationsDisabled?: boolean;
    isTitleLocked?: boolean;
    isTitleDisabled?: boolean;
    isContentLocked?: boolean;
    isAssetsLocked?: boolean;
    isAssetsDisabled?: boolean;
    isTagsLocked?: boolean;
    isTagsDisabled?: boolean;
    isLabelsDisabled?: boolean;
    isMediaLocked?: boolean;
    isPublishTimeLocked?: boolean;
    isPublishOptionsLocked?: boolean;
    returnUrl?: string;
    scheduleDateTimeRange?: ScheduleDateTimeRange;
    duplicatePost?: boolean;
    isContentAndMediaLocked?: boolean;
    postOrigin?: PostOrigin;
    rating?: Rating;
}

export interface ViewState {
    profile?: Profile;
    editorMode: EditorMode;
    postId?: string;
    contentId?: string | null;
    campaignId?: string | null;
    campaignType?: CampaignType | null;
    selectedLocations: LocationUid[];
    selectedLocationGroup?: LocationGroup;
    enabledLocationGroups?: EnabledLocationGroup;
    myLocations?: Location[];
    oldPostLocations?: Location[];
    selectedSources: SelectedSource;
    enabledSources: EnabledSource;
    postType: PostType;
    title?: string;
    content: MetaData[];
    media: any[];
    mediaValidation: MediaUrlValidation[];
    scheduleDateTime: ScheduleDateObject[];
    sourceToPublishTime?: { string: CalendarDateObject } | null;
    scheduleDateTimeRange?: ScheduleDateTimeRange;
    locationSources: LocationSource[];
    publishType: PublishType;
    enabledPublishTypes: PublishType[];
    schedulePostResponse: SchedulePostResponse | SchedulePostsResponse | undefined;
    googleFields: GoogleFields;
    youtubeFields: YoutubeFields;
    urlMetaData: UrlMetaData | null;
    publisherErrors: PublisherError[];
    isShowPostSummary: boolean;
    isSingleLocationUser: boolean;
    isSingleSourceUser: boolean;
    hasSocialPermission: boolean;
    hasApproveAutoPermission: boolean;
    hasProfilePublishPermission: boolean;
    hasTagAddPermission: boolean;
    hasLabelAddPermission: boolean;
    hasLabelViewPermission: boolean;
    hasContentAddGlobalPermission: boolean;
    hasTargetAudiencePermission: boolean;
    hasUserSelectedLocationsManually: boolean;
    hasUserChosenMediaPostType: boolean;
    isSourcesLocked: boolean;
    isLocationsLocked: boolean;
    isLocationsDisabled: boolean;
    isTitleLocked?: boolean; // todo
    isTitleDisabled: boolean;
    isContentLocked?: boolean; // todo
    isAssetsLocked: boolean;
    isAssetsDisabled: boolean;
    isTagsLocked?: boolean; // todo
    isTagsDisabled: boolean;
    isLabelsDisabled: boolean;
    isMediaLocked?: boolean; // todo
    isPublishTimeLocked: boolean;
    isPublishOptionsLocked: boolean;
    isContentAndMediaLocked: boolean;
    isShortenedUrl: boolean;
    promisesInTransit: string[];
    returnUrl: string;
    contentData?: LibraryContent;
    selectedTags: Tag[];
    selectedLabels: Label[];
    selectedLabelIds?: string[];
    targetAudience: { FACEBOOK: FacebookTargetFields };
    duplicatePost: boolean;
    locationTag: LocationTag | null;
    scheduledPostState?: ScheduledPostState;
    isDMLinkAdded: boolean;
    postOrigin?: PostOrigin;
    rating?: Rating;
    publishSourceType?: PublishSourceType;
    isSmartReady?: boolean;
    customizeSourceKey: SourceName | 'ALL_SOURCES';
    sourceStates: SourceStates;
    isCustomizingBySource: boolean;
    mediaPathsToAltText?: MediaPathsToAltText;
}

export interface SourceState {
    content: MetaData[];
    media: any[];
    postType: PostType;
    urlMetaData: UrlMetaData | null;
    googleFields: GoogleFields;
    youtubeFields: YoutubeFields;
    title?: string;
    isShortenedUrl: boolean;
    hasUserChosenMediaPostType: boolean;
    mediaPathsToAltText?: MediaPathsToAltText;
}

export interface SourceStates extends Record<SourceName | 'ALL_SOURCES', SourceState | null> {
    [index: string]: SourceState | null;
}

export interface SelectedSource extends Record<SourceName, boolean> {
    [index: string]: boolean;
}

export interface EnabledSource extends Record<SourceName, boolean> {
    [index: string]: boolean;
}

export interface SourceAvailable extends Record<SourceName, boolean> {
    [index: string]: boolean;
}

export interface SourceStatus extends Record<SourceName, TokenState> {
    [index: string]: TokenState;
}

export interface SourceCharLimit extends Record<SourceName, number> {
    [index: string]: number;
}

export interface SourceMediaLimit extends Record<SourceName, number> {
    [index: string]: number;
}

export interface EnabledLocationGroup extends Record<LocationGroup, boolean> {
    [index: string]: boolean;
}

export interface LocationSourceResponse {
    locationSources: LocationSource[];
}

export interface LocationSource {
    lUid: number;
    lName: string;
    lCode: string;
    sources: SourceStatus;
}

export interface PublisherError {
    source: SourceName | null;
    component: string;
    message: string;
}

export interface Medium {
    $$hashKey?: string;
    bytes?: number;
    cloudinary_url?: string;
    created_at?: string;
    etag?: string;
    format?: string;
    height?: number;
    id?: string;
    index?: 0;
    large_url?: string;
    mediaValidations?: any[];
    medium_url?: string;
    narrow_url?: string;
    original_url?: string;
    public_id?: string;
    signature?: string;
    'small-square_url'?: string;
    small_url?: string;
    sourceClass?: string;
    square_url?: string;
    type?: string;
    wide_url?: string;
    width?: number;
}

export interface BaseDomainMetaDatum {
    agencyId?: number;
    archived?: boolean;
    createdUser?: number;
    dateCreatedOn?: string;
    dateUpdatedOn?: string;
    locationId?: string | string[];
    locationUid?: LocationUid | LocationUid[];
    tenantId?: number;
    updatedUser?: number;
}

export interface CalendarDateObject {
    year: number;
    month: number;
    dayOfMonth: number;
    hour: number;
    minute: number;
    second: number;
    timezone: string;
}

export interface ScheduleDateObject {
    postDate: CalendarDateObject;
    useLocationTimezone: boolean;
}
export interface PostDateMetaData {
    postDate: CalendarDateObject;
    useLocationTimezone: boolean;
    scheduledDateMillis: number;
}

export interface ScheduledPost {
    scheduledPostState?: ScheduledPostState;
    agencyID: number;
    boostedStatus?: any;
    calendarDateObject?: CalendarDateObject;
    camId?: string | null;
    camType?: CampaignType | null;
    conId?: string | null;
    consentInfo?: any;
    createdDate?: number;
    editedFromId?: string | null;
    id?: string;
    locationFacets?: any[];
    locations?: Location[];
    locationUids?: number[] | null;
    medium?: Medium[];
    postDate?: ScheduleDateObject | null;
    postList: PostObjectAndSources[];
    properties?: any;
    scheduleDate?: number;
    scheduledAdId?: any;
    tenantID: number;
    uploadType?: number;
    userID: number;
    watchwords?: any;
    targetAudienceBySource?: { FACEBOOK: FacebookTargetFields };
    multiPublish?: boolean;
    postDateMetadataList?: PostDateMetaData[];
    locationTag?: LocationTag;
    dark?: boolean;
    labelIds?: string[];
    postOrigin?: PostOrigin;
    publishType?: PublishSourceType;
    sourceToPublishTime?: { string: CalendarDateObject };
}

export enum ScheduledPostState {
    DRAFT = 1,
    ORIGINAL = 2,
}

export enum MediaType {
    SINGLE_IMAGE = 31,
    MULTI_IMAGE = 32,
    SINGLE_VIDEO = 33,
    MULTI_IMAGE_MULTI_MESSAGE = 36,
    MULTI_IMAGE_MULTI_VIDEO = 37,
    MULTI_IMAGE_ALBUM = 38,
}

export interface ContentPostMedia {
    customizedMediaPost?: any | null;
    standardMediaPost: {
        mediaType: MediaType;
        instagramMessage?: string | null;
        mediaURLs: string[] | null;
        mentionMetaData?: MentionMetaDatum[] | null; // TODO: What does this look like???
        hashtagMetaData?: HashtagMetaDatum[] | null;
        message: string | null;
        shortMessage?: string | null;
        title?: string | null;
        plainText?: string | null;
    };
}

export interface ContentPostLink {
    message: string;
    linkUrl: string;
    mentionMetaData?: MentionMetaDatum[] | null;
    hashtagMetaData?: HashtagMetaDatum[] | null;
    plainText?: string | null;
}

export interface ContentPostText {
    message: string;
    mentionMetaData?: MentionMetaDatum[] | null;
    hashtagMetaData?: HashtagMetaDatum[] | null;
    plainText?: string | null;
}

export interface ContentPostObject {
    post: {
        link?: ContentPostLink | null;
        text?: ContentPostText | null;
        media?: ContentPostMedia | null;
    };
    sources: SourceEnum[];
}

export interface ContentPost {
    postType: ContentPostType;
    posts: ContentPostObject[];
    callToAction?: GoogleCallToAction | null;
    event?: GoogleEvent | null;
    alertType?: GoogleAlertType | null;
    title?: string;
    kidsVideo?: boolean;
    embedVideo?: boolean;
    publishToFeed?: boolean;
}

export enum ContentPostType {
    TEXT = 1,
    LINK = 2,
    MEDIA = 3,
}

export interface LibraryContent {
    approvedStatus?: number;
    archived?: boolean | null;
    baseDomainMetaDatum: BaseDomainMetaDatum;
    calendarDateObject: CalendarDateObject;
    campaignId: string;
    consentInfo?: any | null;
    content: ContentPost;
    description?: string | null;
    id: string;
    locked: boolean;
    medium: Medium[] | null;
    numViews?: number;
    origin: ContentOrigin;
    properties?: any | null;
    removed: boolean;
    rssId: string | null;
    scheduleDate: string | null;
    scheduleDateObject: ScheduleDateObject | null;
    tagMetaData?: TagMetaDatum[] | null;
    title: string | null;
    duplicatedFromId: string | null;
    labelIds?: string[];
    smartConfigId?: string;
    smartReady?: boolean;
}

export interface ContentMetaDatum {
    baseDomainMetaDatum: BaseDomainMetaDatum;
    content: ContentPost;
    id?: string;
    campaignId?: string;
    origin?: ContentOrigin;
    scheduleDateObject?: ScheduleDateObject;
    calendarDateObject?: CalendarDateObject;
    title?: string;
    tagMetaData?: TagMetaDatum[];
    labelIds?: string[];
    duplicatedFromId?: string | null;
    postOrigin: PostOrigin;
    ratingID?: string;
    smartConfigId?: string;
    smartReady?: boolean;
    mediaPathsToAltText?: string[][]; // backend expects string[][] because mongo doesn't allow '.' in map keys
}

export interface PublishContent {
    scheduledContent: {
        contentMetaDatum: ContentMetaDatum;
        scheduleDate: ScheduleDateObject;
        calendarDateObject: CalendarDateObject;
        locationIds: string[];
        properties: [
            { name: 'campaignId'; value: string },
            { name: 'contentId'; value: string },
            { name: 'campaignType'; value: string },
            { name: 'savedFrom'; value: 'PUBLISHER_V2' },
        ];
    };
    tenantId: number;
}

export interface SourceMediaValidation {
    errorMessages?: string[];
    valid: boolean;
}

export interface SourceValidation extends Record<SourceName, SourceMediaValidation> {
    [index: string]: SourceMediaValidation;
}

export interface MediaUrlValidation {
    url: string;
    metaData?: any | null;
    sourceValidation: SourceValidation;
    publishPostType: PublishPostType;
}

export interface MediaLimitation {
    source: SourceName;
    summary: string;
    image?: string[];
    video?: string[];
}

export interface ReducerAction {
    type: string;
    payload: any;
}

export enum SocialPostState {
    PostFailed = 1,
    Rejected = 2,
    Canceled = 3,
    PendingApproval = 4,
    Approved = 5,
    PostSucceeded = 6,
}

export enum ResponseCode {
    Success = 1,
    Failed = 2,
    SuccessWithErrors = 3,
    SuccessWithMessage = 4,
}

export interface Error {
    field: string;
    code: string;
    message: string;
    value?: any;
}

export interface Response {
    responseCode: ResponseCode;
    errors?: Error[] | null;
    message?: string | null;
    transientError?: any;
    statusCode?: any;
}

export interface SourceResult {
    response: Response;
    source: SourceName;
    slpState: SocialPostState | null;
    id?: string | null;
    errorCode?: number | null;
    slpErrorCode?: number | null;
    slpErrorMessage?: string | null;
}

export interface LocationResponse {
    locationCode: string;
    pendingApproval: boolean;
    sourceResults: SourceResult[];
    locationId: string;
    locationUid: LocationUid;
    locationName: string;
}

export interface CreatePost {
    scheduledPost: ScheduledPost;
    locationFacets: Filter[];
    options: SchedulePostOptions;
    tenantId: number;
}

export interface CreatePosts {
    scheduledPost: ScheduledPost;
    locationFacets: Filter[];
    options: SchedulePostOptions;
    tenantId: number;
    postDateMetadataList: PostDateMetaData[];
}

export interface UpdatePost {
    oldScheduledPostId: string;
    oldPostFacets: Filter[];
    newScheduledPost: ScheduledPost;
    newLocationFacets: Filter[];
    options: SchedulePostOptions;
    tenantId: number;
}

export interface SchedulePostsResponse {
    response: Response;
    schedulePostV2ResponseList: SchedulePostResponse[];
}

export interface SchedulePostResponse {
    response: Response;
    id: string;
    postDate: ScheduleDateObject;
    locationSaves: LocationResponse[] | null;
}

export interface NewDraftPost {
    scheduledPost: ScheduledPost;
}

export interface EditedDraftPost {
    scheduledPost: ScheduledPost;
    scheduledPostId: string;
}

export interface DraftPostResponse {
    response: Response;
    id: string;
}

export enum GoogleButtonType {
    BOOK = 1,
    ORDER = 2,
    SHOP = 3,
    LEARN_MORE = 4,
    SIGN_UP = 5,
    CALL = 6,
    GET_OFFER = 7,
}

export enum GoogleAlertType {
    ALERT_TYPE_UNSPECIFIED = 1,
    COVID_19 = 2,
}

export interface GoogleCallToAction {
    buttonType: GoogleButtonType;
    link: string | null;
}

export interface GoogleButtonOption {
    value: GoogleButtonType;
    label: string;
    placeholder: string;
}

export interface GoogleEvent {
    title: string;
    startDate: number;
    endDate: number;
}

export interface GoogleFields {
    callToAction?: GoogleCallToAction | null;
    alertType?: GoogleAlertType | null;
    event?: GoogleEvent | null;
}

export interface YoutubeFields {
    kidsVideo: boolean;
    embedVideo: boolean;
    publishToFeed: boolean;
}

export interface UrlMetaData {
    title: string;
    description: string;
    url: string;
    images: { url: string; height?: number; width?: number }[];
    type?: string | null;
}

export enum AssetType {
    TEXT = 1,
    LINK = 2,
    PICTURE = 3,
    VIDEO = 4,
}

export interface Tag {
    id: string;
    name: string;
}

export interface TagMetaDatum {
    tag: Tag;
    tenantId?: number;
    createUser?: number;
    updateUser?: number;
    count?: number;
    mediaUrls?: string[];
}

export enum LabelSource {
    System = 1,
    DataScience = 2,
    User = 3,
}

export interface Label {
    id: string;
    name: string;
}

export interface LabelMetaDatum {
    label: Label;
    tenantId?: number;
    moduleIds?: string[];
    labelSource: LabelSource;
    count?: number;
    mediaUrls?: string[];
}

export interface Asset {
    id: string;
    baseDomainMetaDatum: BaseDomainMetaDatum;
    assetType: AssetType;
    tagMetaData: TagMetaDatum[];
    campaignId: string;
    locked: boolean;
    title: string;
    description: string;
    mediaUrl: string;
    contentTitle: string;
    contentDescription: string;
    unlockStartDate: string;
    unlockEndDate: string;
    archived: boolean;
    medium?: any[]; //  contains thumbnail for video asset
    linkUrl?: string;
    labelIds?: any[];
}

export enum TagType {
    RSS = 1,
    CUSTOM = 2,
    CAMPAIGN = 3,
    ASSET = 4,
}

export enum KeyPhraseType {
    Profane = 0, // Profane keywords (will show an error and not allow posts)
    Rejected = 10, // Warning keywords (will show a warning but allow posts)
}

export interface KeyPhrase {
    keyPhraseText: string;
    sourceBegin: number;
    sourceEnd: number;
    keyPhraseType: KeyPhraseType;
}

export enum CampaignType {
    PULL = 1, // DEPRECATED, DO NOT USE
    PUSH = 2,
    CURATION = 3,
    AD = 4,
    LOCAL_CURATION = 5,
    SHADOW = 6,
}

export interface Location {
    id: string;
    uid: LocationUid;
    code: string;
}
export interface SchedulePostOptions {
    partialPosting: boolean;
    autoApproved: boolean;
    ignoreMissingAuth: boolean;
    rejectLevel: number;
}

export enum SourceEnum {
    ALL = 0,
    FACEBOOK = 1,
    TWITTER = 2,
    GOOGLE_PLUS = 3,
    YOUTUBE = 4,
    INSTAGRAM = 5,
    FORUM = 6,
    NEWS = 7,
    BLOG = 8,
    REVIEW = 9,
    VIDEO = 10,
    GENERAL = 11,
    IMAGE = 12,
    GOOGLE_PLACES = 13,
    LINKEDIN = 14,
    BING = 15,
    SNAPCHAT = 16,
    TIKTOK = 17,
}

export interface CampaignAndLocation {
    id: string;
    campaignId: string;
    baseDomainMetaDatum: BaseDomainMetaDatum;
}

export interface CampaignMetaDatum {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    baseDomainMetaDatum: BaseDomainMetaDatum;
    tagMetaData: TagMetaDatum[] | null;
    removed?: boolean;
    campaignType?: CampaignType;
    imageUrl?: string | null;
    description: string;
    facets: { location: string[] };
    emailSchedules?: any[] | null;
    emailSchedulesSentNow?: any[] | null;
    dripStarted?: boolean;
    coEditors: any[] | null;
    sourcesAndCounts: any;
    color: string;
}

export interface ScheduleDateTimeRange {
    minDate?: string; // ISO string
    maxDate?: string; // ISO string
}

export interface SaveContentResponse {
    id: string;
    response: Response;
}
// location code
export interface SaveContent {
    content: {
        contentMetaDatum: ContentMetaDatum;
        facets: { location: string[] } | { g: [] };
    };
    tenantId: number;
}

export enum ContentOrigin {
    RSS = 1,
    CUSTOM = 2,
    CAMPAIGN = 3,
}

export interface PostObject {
    text: string;
    mediaPaths?: string[];
    mentionMetaData?: MentionMetaDatum[] | null;
    hashtagMetaData?: HashtagMetaDatum[] | null;
    link?: { url: string; description: string; name: string };
    callToAction?: GoogleCallToAction | null;
    event?: GoogleEvent | null;
    alertType?: GoogleAlertType | null;
    title?: string;
    kidsVideo?: boolean;
    embedVideo?: boolean;
    publishToFeed?: boolean;
    macroMetaData?: MarcoMetaDatum[];
    dmLink?: boolean | null;
    story?: boolean | null;
    reel?: boolean | null;
    mediaPathsToAltText?: string[][]; // backend expects string[][] because mongo doesn't allow '.' in map keys
}

export interface MarcoMetaDatum {
    name: string;
    userDefinedVariables: { name: string; value: string }[];
}

export interface PostObjectAndSources {
    post: PostObject;
    sources: SourceName[];
}

export interface SaveTag {
    tag: { tag: { name: string } };
}

export interface SaveTagResponse {
    id: string;
    response: Response;
}

export interface SaveLabelsResponse {
    response: Response;
    labels: Label[];
}

export interface OptimalPublishTimesResponse {
    response: Response;
    publishTime: PublishTime;
}

export interface PublishTime {
    source: SourceName;
    day: DayOfWeek;
    timeToRank: Record<string, number>;
}

export type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

export interface MentionMetaDatum {
    source: SourceName; // The source of the social media site that the mention belongs to
    text: string; // The text that will be hyperlinked to the external page
    externalId?: string; // The externalId of the page (Facebook is the page id, Twitter is the @screenname)
    link: string; // The link to the external page
    startIndex: number; // Start index of the substring that will be replaced
    endIndex: number; // End index of the substring that will be replaced
}

export interface SearchMentionResponse {
    response: Response;
    facebookPages: FacebookPage[];
    twitterProfiles: TwitterProfile[];
}

export interface ValidateMentionResponse {
    response: Response;
    isValid: boolean;
    externalId: string;
    displayName: string;
}

export interface MentionData {
    facebookPages: FacebookPage[];
    twitterProfiles: TwitterProfile[];
}

export interface FacebookPage {
    id: string;
    name: string;
    isUnclaimed: boolean;
    link: string;
    verificationStatus: string;
}

export interface TwitterProfile {
    id: string;
    screenname: string;
    name: string;
    picture: string;
    link: string;
    verificationStatus: boolean;
}

export interface HashtagMetaDatum {
    source: SourceName; // The source of the social media site that the hashtag belongs to
    text: string; // The text that will be hyperlinked to the external page
    link: string; // The link to the external page
    startIndex: number; // Start index of the substring that will be replaced
    endIndex: number; // End index of the substring that will be replaced
}

export interface SearchHashtagResponse {
    response: Response;
    hashtags: Hashtag[];
}

export interface HashtagData {
    hashtags: Hashtag[];
}

export interface Hashtag {
    text: string;
    posts: number;
    likes: number;
    weight: number;
}

export enum HashtagSearchType {
    TEXT = 1,
    IMAGE = 2,
    VIDEO = 3,
}
export interface HashtagGroup {
    id: string;
    name: string;
    hashtags: string[];
}
export interface SearchHashtagGroupResponse {
    response: Response;
    hashtagGroups: HashtagGroup[];
}

export interface SaveHashtagGroupResponse {
    response: Response;
    hashtagGroup: HashtagGroup;
}

export interface FacebookTargetFields {
    interests?: TargetInterest[];
    ages?: AgeRange;
    genders?: Gender[];
    relationshipStatuses?: RelationshipStatus[];
    educationalStatuses?: EducationalStatus[];
    geoLocations?: FacebookGeoLocation;
    locales?: FacebookLocale[];
    isUnpublished?: boolean;
}

export interface TargetInterest {
    key: any;
    name: string;
    audienceSize: number;
    values?: TargetInterest[];
}
export interface AgeRange {
    ageMin: number;
    ageMax: number;
}
export interface FacebookLocale {
    key: number;
    name: string;
}
export interface GeoLocation {
    key: string;
    name: string;
    type?: string;
    region?: string;
    country?: string;
}
export interface FacebookGeoLocation {
    countries?: GeoLocation[];
    regions?: GeoLocation[];
    cities?: GeoLocation[];
}
export enum Gender {
    MALE = 1,
    FEMALE = 2,
}

export enum RelationshipStatus {
    SINGLE = 1,
    IN_RELATIONSHIP = 2,
    MARRIED = 3,
    ENGAGED = 4,
    NOT_SPECIFIED = 6,
    IN_A_CIVIL_UNION = 7,
    IN_A_DOMESTIC_PARTNERSHIP = 8,
    IN_AN_OPEN_RELATIONSHIP = 9,
    ITS_COMPLICATED = 10,
    SEPARATED = 11,
    DIVORCED = 12,
    WIDOWED = 13,
}

export enum EducationalStatus {
    HIGH_SCHOOL = 1,
    UNDERGRADUATE = 2,
    ALUM = 3,
    HIGH_SCHOOL_GRAD = 4,
    SOME_COLLEGE = 5,
    ASSOCIATE_DEGREE = 6,
    IN_GRAD_SCHOOL = 7,
    SOME_GRAD_SCHOOL = 8,
    MASTER_DEGREE = 9,
    PROFESSIONAL_DEGREE = 10,
    DOCTORATE_DEGREE = 11,
    UNSPECIFIED = 12,
    SOME_HIGH_SCHOOL = 13,
}

// twitter check in
export interface LocationTag {
    id: string;
    name: string;
    fullName: string;
    country: string;
    countryCode: string;
    placeType: string;
}

export interface ShareReviewConfig {
    media: Array<any>;
}

export enum ExtractorSource {
    Facebook = 1,
    Embedly = 2,
}

export enum PostOrigin {
    DEFAULT = 1,
    REVIEW = 2,
    POST_SUGGESTION = 3,
    SURVEY = 5,
}

export interface Rating {
    ratingID: string;
    tenantID: number;
    userID: number;
    ratedDate: string;
}

export const SourceReadableNameMap: { [sourceName: string]: string } = {
    FACEBOOK: 'Facebook',
    INSTAGRAM: 'Instagram',
    TWITTER: 'Twitter',
    GOOGLE_PLACES: 'Google Business Profile',
    LINKEDIN: 'LinkedIn',
    YOUTUBE: 'YouTube',
    IG_STORIES: 'Instagram Stories',
    IG_REELS: 'Instagram Reels',
    TIKTOK: 'TikTok',
};

export interface MediaPathsToAltText extends Record<string, string> {
    [index: string]: string;
}

export interface MediaAltText {
    mediaPath: string;
    altText: string;
}
